import { ChangeDetectionStrategy, Component, inject, input, OnDestroy, OnInit, output, signal } from '@angular/core';
import { email, FormField, form, required } from '@angular/forms/signals';
import { Subscription } from 'rxjs';
import { BriefResponseDto } from '@/app/common/general-types/briefs.dto';
import { CreateRecruitGroupDto, RecruitGroupResponseDto } from '@/app/common/general-types/recruit-groups.dto';
import { embedRecruitGroupReview, parseRecruitGroupReview } from '@/app/common/general-types/recruit-groups.review';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { BriefService } from '@/app/services/brief/brief.service';
import { RecruitGroupService } from '@/app/services/recruit-group/recruit-group.service';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { ViewportOverlayDirective } from '@/app/common/directives/viewport-overlay/viewport-overlay.directive';

@Component({
  selector: 'cmg-share-group',
  imports: [FormField],
  hostDirectives: [ViewportOverlayDirective],
  templateUrl: './share-group.html',
  styleUrl: './share-group.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.escape)': 'closeShare()',
  },
})
export class ShareGroup implements OnInit, OnDestroy {
  readonly group = input.required<RecruitGroupResponseDto>();
  readonly close = output<RecruitGroupResponseDto | undefined>();

  readonly shareUrl = signal('');
  readonly copiedLink = signal(false);
  readonly sendingEmail = signal(false);
  readonly emailSent = signal(false);
  readonly resolvingLink = signal(true);
  readonly allowReviewerInvite = signal(false);
  readonly selectedInviteBriefId = signal('');
  readonly inviteBriefs = signal<BriefResponseDto[]>([]);
  readonly loadingBriefs = signal(false);
  readonly savingInviteSettings = signal(false);
  readonly inviteSettingsSaved = signal(false);

  readonly shareModel = signal({
    email: '',
    message: '',
  });
  readonly shareForm = form(this.shareModel, (schemaPath) => {
    required(schemaPath.email, { message: 'Email is required' });
    email(schemaPath.email, { message: 'Enter a valid email address' });
  });

  private readonly subscriptionList = new Subscription();
  private readonly recruitGroupService = inject(RecruitGroupService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly briefService = inject(BriefService);
  private readonly toastNotificationService = inject(ToastNotificationService);
  private lastUpdatedGroup = signal<RecruitGroupResponseDto | undefined>(undefined);
  private inviteSaveQueued = false;

  ngOnInit(): void {
    const group = this.group();
    const review = parseRecruitGroupReview(group);
    this.allowReviewerInvite.set(Boolean(group.allowReviewerInvite) || Boolean(review.allowReviewerInvite));
    this.selectedInviteBriefId.set(group.reviewerInviteBriefId || review.reviewerInviteBriefId || '');
    this.prepareShareLink();
    this.loadInviteBriefs();
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  copyShareLink(): void {
    const url = this.shareUrl();
    if (!url) {
      return;
    }

    void navigator.clipboard.writeText(url).then(() => {
      this.copiedLink.set(true);
      void this.toastNotificationService.alert({
        title: 'Link copied',
        message: 'Anyone with this link can review the shortlist.',
        type: 'success',
        icon: 'success',
      });
    }).catch(() => {
      void this.toastNotificationService.alert({
        title: 'Could not copy link',
        message: 'Copy the link from the field instead.',
        type: 'error',
        icon: 'error',
      });
    });
  }

  sendEmail(): void {
    if (this.shareForm().invalid() || !this.shareUrl()) {
      return;
    }

    this.sendingEmail.set(true);
    const group = this.lastUpdatedGroup() ?? this.group();
    const payload = {
      email: this.shareModel().email.trim(),
      message: this.shareModel().message.trim() || undefined,
      shareUrl: this.shareUrl(),
    };

    this.subscriptionList.add(
      this.recruitGroupService.emailShareLink(group.id, payload).subscribe({
        next: () => {
          this.sendingEmail.set(false);
          this.emailSent.set(true);
        },
        error: () => {
          this.openMailtoFallback(group.groupName, payload.email, payload.message, payload.shareUrl);
          this.sendingEmail.set(false);
          this.emailSent.set(true);
        },
      }),
    );
  }

  closeShare(): void {
    this.close.emit(this.groupWithCurrentInviteSettings(this.lastUpdatedGroup() ?? this.group()));
  }

  ignoreBackdrop(event: Event): void {
    event.stopPropagation();
  }

  toggleAllowReviewerInvite(): void {
    this.allowReviewerInvite.update((enabled) => !enabled);
    this.persistInviteSettings();
  }

  onInviteBriefChange(event: Event): void {
    const nextId = (event.target as HTMLSelectElement).value;
    if (nextId === this.selectedInviteBriefId()) {
      return;
    }
    this.selectedInviteBriefId.set(nextId);
    this.persistInviteSettings();
  }

  private firstBrandId(): string | undefined {
    const brands = this.currentUserService.myBrands ?? [];
    for (const brand of brands) {
      const candidate = brand as { id?: string; businessAccount?: { id?: string } };
      const id = candidate.id ?? candidate.businessAccount?.id;
      if (id?.trim()) {
        return id;
      }
    }
    return undefined;
  }

  private prepareShareLink(): void {
    const group = this.group();
    this.resolvingLink.set(true);

    const brandId = this.currentUserService.focussedBrand?.id as string | undefined
      ?? this.firstBrandId();
    if (brandId && !group.businessAccountId?.trim()) {
      this.attachBrandThenResolveLink(group, brandId);
      return;
    }

    this.resolveShareLink(group.id);
  }

  private attachBrandThenResolveLink(group: RecruitGroupResponseDto, brandId: string): void {
    const source = this.groupWithCurrentInviteSettings(this.lastUpdatedGroup() ?? group);
    const updateDto = this.toGroupUpdateDto(source, {
      businessAccountId: brandId,
      approvalStatus: 'pending_review',
    });

    this.subscriptionList.add(
      this.recruitGroupService.updateRecruitGroup(group.id, updateDto).subscribe({
        next: (updated) => {
          this.lastUpdatedGroup.set(this.groupWithCurrentInviteSettings(updated));
          this.resolveShareLink(updated.id);
        },
        error: () => {
          this.resolveShareLink(group.id);
        },
      }),
    );
  }

  private resolveShareLink(groupId: string): void {
    this.subscriptionList.add(
      this.recruitGroupService.createOrGetShareLink(groupId).subscribe({
        next: (response) => {
          const token = response.token || groupId;
          this.shareUrl.set(this.recruitGroupService.buildShareUrl(token));
          this.resolvingLink.set(false);
        },
        error: () => {
          this.shareUrl.set(this.recruitGroupService.buildShareUrl(groupId));
          this.resolvingLink.set(false);
        },
      }),
    );
  }

  private loadInviteBriefs(): void {
    const brandId = this.currentUserService.focussedBrand?.id as string | undefined
      ?? this.firstBrandId();
    if (!brandId) {
      return;
    }

    this.loadingBriefs.set(true);
    this.subscriptionList.add(
      this.briefService.getBriefsForBusinessAccount(brandId, { statuses: [0, 1] }).subscribe({
        next: (response) => {
          const briefs = Array.isArray(response)
            ? response
            : (response?.data ?? []);
          this.inviteBriefs.set(briefs);
          this.loadingBriefs.set(false);
          if (this.selectedInviteBriefId()) {
            this.selectedInviteBriefId.set(this.selectedInviteBriefId());
          }
          this.ensureSelectedInviteBrief(brandId, briefs);
        },
        error: () => {
          this.loadingBriefs.set(false);
        },
      }),
    );
  }

  private ensureSelectedInviteBrief(brandId: string, briefs: BriefResponseDto[]): void {
    const selectedId = this.selectedInviteBriefId();
    if (!selectedId || briefs.some((brief) => brief.id === selectedId)) {
      return;
    }

    this.subscriptionList.add(
      this.briefService.getBriefById(selectedId).subscribe({
        next: (brief) => {
          if (brief?.id && brief.businessAccountId === brandId) {
            this.inviteBriefs.update((current) => [brief, ...current.filter((item) => item.id !== brief.id)]);
            this.selectedInviteBriefId.set(brief.id);
          }
        },
        error: () => {
          // Keep the saved id; the reviewer invite stays off until a live brief is chosen.
        },
      }),
    );
  }

  private persistInviteSettings(): void {
    if (this.savingInviteSettings()) {
      this.inviteSaveQueued = true;
      this.lastUpdatedGroup.set(this.groupWithCurrentInviteSettings(this.lastUpdatedGroup() ?? this.group()));
      return;
    }

    const group = this.lastUpdatedGroup() ?? this.group();
    this.lastUpdatedGroup.set(this.groupWithCurrentInviteSettings(group));
    this.savingInviteSettings.set(true);
    const review = parseRecruitGroupReview(group);
    const updateDto = this.toGroupUpdateDto(group, {
      groupDescription: embedRecruitGroupReview(group.groupDescription, {
        ...review,
        allowReviewerInvite: this.allowReviewerInvite(),
        reviewerInviteBriefId: this.selectedInviteBriefId() || undefined,
      }),
      allowReviewerInvite: this.allowReviewerInvite(),
      reviewerInviteBriefId: this.selectedInviteBriefId() || undefined,
    });

    this.subscriptionList.add(
      this.recruitGroupService.updateRecruitGroup(group.id, updateDto).subscribe({
        next: (updated) => {
          this.lastUpdatedGroup.set(this.groupWithCurrentInviteSettings(updated));
          this.savingInviteSettings.set(false);
          this.inviteSettingsSaved.set(true);
          if (this.inviteSaveQueued) {
            this.inviteSaveQueued = false;
            this.persistInviteSettings();
          }
        },
        error: () => {
          this.savingInviteSettings.set(false);
          this.inviteSettingsSaved.set(false);
          this.inviteSaveQueued = false;
          void this.toastNotificationService.alert({
            title: 'Could not save invite setting',
            message: 'Choose a brief again so the reviewer can invite approved creators.',
            type: 'error',
            icon: 'error',
          });
        },
      }),
    );
  }

  private groupWithCurrentInviteSettings(group: RecruitGroupResponseDto): RecruitGroupResponseDto {
    const review = parseRecruitGroupReview(group);
    return {
      ...group,
      allowReviewerInvite: this.allowReviewerInvite(),
      reviewerInviteBriefId: this.selectedInviteBriefId() || undefined,
      groupDescription: embedRecruitGroupReview(group.groupDescription, {
        ...review,
        allowReviewerInvite: this.allowReviewerInvite(),
        reviewerInviteBriefId: this.selectedInviteBriefId() || undefined,
      }),
    };
  }

  private toGroupUpdateDto(
    group: RecruitGroupResponseDto,
    patch: Partial<CreateRecruitGroupDto>,
  ): CreateRecruitGroupDto {
    return {
      groupName: group.groupName,
      groupDescription: group.groupDescription,
      createdBy: group.createdBy,
      isGlobal: group.isGlobal,
      businessAccountId: group.businessAccountId,
      agencyAccountId: group.agencyAccountId,
      group_type: group.group_type,
      syncWithNoodle: group.syncWithNoodle,
      sentToNoodle: group.sentToNoodle,
      briefId: group.briefId,
      briefType: group.briefType,
      ...patch,
    };
  }

  private openMailtoFallback(groupName: string, emailAddress: string, message: string | undefined, shareUrl: string): void {
    const subject = encodeURIComponent(`Review this creator shortlist: ${groupName}`);
    const note = message?.trim() ? `${message.trim()}\n\n` : '';
    const body = encodeURIComponent(
      `${note}${shareUrl}\n\nYou can approve or decline names without creating an account. Add your first name so the team knows who reviewed.`,
    );
    window.location.href = `mailto:${emailAddress}?subject=${subject}&body=${body}`;
  }
}
